import React, { useEffect, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { MapContainer, TileLayer, CircleMarker, Tooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { PernoiteOcorrencia, buscarPernoites } from '../api';

interface Props {
  idOcorrencia: string | null;
  idEmpresa: string;
  onClose: () => void;
}

const SIMBOLO: Record<string, { texto: string; cor: string; titulo: string }> = {
  endereco: { texto: '✓', cor: '#15803d', titulo: 'Dormiu no endereço' },
  fora: { texto: '✗', cor: '#c2410c', titulo: 'Dormiu fora' },
  sem_sinal: { texto: '?', cor: '#94a3b8', titulo: 'Sem sinal do rastreador' },
};

const dia = (iso: string) => {
  const [, m, d] = iso.substring(0, 10).split("-");
  return `${d}/${m}`;
};

const distanciaTexto = (km?: number | null) =>
  km == null ? '' : km < 1 ? `${Math.round((km * 1000) / 50) * 50} m` : `${km.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} km`;

// Histórico das noites de uma ocorrência de pernoite e o mapa: o endereço do locatário e onde o veículo tem dormido.
const ModalPernoite: React.FC<Props> = ({ idOcorrencia, idEmpresa, onClose }) => {
  const [dados, setDados] = useState<PernoiteOcorrencia | null>(null);
  const [erro, setErro] = useState(false);

  useEffect(() => {
    if (!idOcorrencia) return;
    setDados(null);
    setErro(false);
    buscarPernoites(idOcorrencia, idEmpresa).then(setDados).catch(() => setErro(true));
  }, [idOcorrencia, idEmpresa]);

  const temEndereco = dados?.latitudeEndereco != null && dados?.longitudeEndereco != null;
  const temFora = dados?.latitudeFora != null && dados?.longitudeFora != null;

  return (
    <Modal show={!!idOcorrencia} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 18 }}>
          Pernoite {dados ? `· ${dados.placa} · ${dados.nomeLocatario}` : ''}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {erro && <div style={{ color: '#b91c1c' }}>Não foi possível carregar as noites desta ocorrência.</div>}
        {!erro && !dados && <div style={{ color: '#64748b' }}>Carregando...</div>}
        {dados && (
          <>
            <div style={{ fontSize: 14, marginBottom: 4 }}>
              <strong>Dormiu fora em {dados.noitesFora} das últimas {dados.noitesConsideradas} noites.</strong>
              {temFora && <> Tem passado a madrugada a cerca de {distanciaTexto(dados.distanciaForaKm)} do endereço.</>}
            </div>
            <div style={{ fontSize: 12.5, color: '#64748b', marginBottom: 10 }}>Endereço do locatário: {dados.endereco || 'não informado'}</div>

            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
              {dados.noites.map(n => {
                const s = SIMBOLO[n.situacao] ?? SIMBOLO.sem_sinal;
                return (
                  <div key={n.noite} title={`${dia(n.noite)}: ${s.titulo}`} style={{ textAlign: 'center', minWidth: 42 }}>
                    <div style={{
                      width: 34, height: 34, margin: '0 auto', borderRadius: 8, background: s.cor, color: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700,
                    }}>{s.texto}</div>
                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{dia(n.noite)}</div>
                  </div>
                );
              })}
              {dados.noites.length === 0 && <div style={{ fontSize: 13, color: '#64748b' }}>Ainda não há noites apuradas.</div>}
            </div>

            {temEndereco && (
              <div style={{ height: 320, borderRadius: 8, overflow: 'hidden', border: '1px solid #e0e5ee' }}>
                <MapContainer
                  center={[dados.latitudeEndereco!, dados.longitudeEndereco!]}
                  zoom={temFora ? 11 : 15}
                  style={{ height: '100%', width: '100%' }}
                  bounds={temFora ? [[dados.latitudeEndereco!, dados.longitudeEndereco!], [dados.latitudeFora!, dados.longitudeFora!]] : undefined}
                  boundsOptions={{ padding: [40, 40] }}
                >
                  <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  <CircleMarker center={[dados.latitudeEndereco!, dados.longitudeEndereco!]} radius={9}
                    pathOptions={{ color: '#fff', weight: 2, fillColor: '#1F467E', fillOpacity: 1 }}>
                    <Tooltip permanent direction="top" offset={[0, -8]}>Endereço do locatário</Tooltip>
                  </CircleMarker>
                  {temFora && (
                    <CircleMarker center={[dados.latitudeFora!, dados.longitudeFora!]} radius={9}
                      pathOptions={{ color: '#fff', weight: 2, fillColor: '#c2410c', fillOpacity: 1 }}>
                      <Tooltip permanent direction="top" offset={[0, -8]}>Onde tem dormido</Tooltip>
                    </CircleMarker>
                  )}
                </MapContainer>
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onClose}>Fechar</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalPernoite;
